import React from 'react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTheme } from '../../../hooks/useTheme';

interface PieChartProps {
  data: any[];
  title: string;
  loading: boolean;
  valueLabel?: string;
}

const PieChart: React.FC<PieChartProps> = ({ data, title, loading, valueLabel = 'unidades' }) => {
  const theme = useTheme();
  
  // Debug: Ver qué datos llegan
  console.log('🔍 PieChart - Datos recibidos:', data);
  
  if (loading || !data || data.length === 0) {
    return <div>Sin datos</div>;
  }

  // Transformar y validar datos
  const transformedData = data
    .map(item => ({
      name: item.name || item.category || item.product_category || 'Sin categoría',
      value: item.value || item.total_line_amount || item.quantity_sold || 0
    }))
    .filter(item => {
      // Filtrar valores NaN, null, undefined o negativos
      const isValid = !isNaN(item.value) && 
                     item.value !== null && 
                     item.value !== undefined && 
                     item.value >= 0;
      if (!isValid) {
        console.warn(`⚠️ Valor inválido filtrado en PieChart:`, item);
      }
      return isValid;
    });

  if (transformedData.length === 0) {
    return <div>Sin datos válidos</div>;
  }

  // Debug: Ver datos transformados
  console.log('📊 PieChart - Datos transformados:', transformedData);

  const chartColors = theme.getChartColors(transformedData.length);

  return (
    <div style={{ width: '100%', height: '300px' }}>
      <ResponsiveContainer>
        <RechartsPieChart>
          <Pie
            data={transformedData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill={theme.colors.chart.primary}
            dataKey="value"
          >
            {transformedData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={chartColors[index % chartColors.length]}
              />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{
              backgroundColor: theme.colors.background.card,
              border: `1px solid ${theme.colors.text.muted}`,
              borderRadius: theme.radius.md,
              boxShadow: theme.shadows.md,
            }}
            labelStyle={{ color: theme.colors.text.primary }}
            formatter={(value: any, name: any, props: any) => {
              // Calcular el porcentaje basado en el valor y el total
              const total = transformedData.reduce((sum, item) => sum + item.value, 0);
              const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
              
              return [
                `${value} ${valueLabel} (${percentage}%)`,
                name
              ];
            }}
          />
          <Legend 
            wrapperStyle={{
              color: theme.colors.text.secondary,
              fontSize: 12,
            }}
          />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChart;
